<template>
	<div class="flex w-full justify-end h-full">
		<div
			class="w-full max-w-[600px] bg-ihh-white xs:rounded-l-xl overflow-y-auto overflow-x-hidden transform transition-transform duration-300 ease-in-out"
			:class="store.showBag ? '' : 'translate-x-full'"
			@click.stop
		>
			<!-- Bag Header -->
			<div class="sticky top-0 z-40 bg-ihh-white w-full">
				<div class="flex w-full py-6 px-6 items-center">
					<h2 class="text-ihh-midnight-gray font-semibold text-2xl">Your Bag</h2>
					<div class="flex ml-auto">
						<button @click="toggleBag" class="group w-4 h-4 justify-around flex text-xl py-4 pl-4 pr-6 sm:pr-4 rounded-full hover:cursor-pointer">
							<div class="m-auto items-center flex w-full justify-center">
								<div class="rotate-45 absolute rounded bg-ihh-slate-gray py-px px-2.5"></div>
								<div class="-rotate-45 rounded bg-ihh-slate-gray py-px px-2.5"></div>
							</div>
						</button>
					</div>
				</div>

				<div v-if="store.cartTotalItems" class="flex justify-center items-center gap-3 px-6 pb-10 w-full h-14">
					<router-link
						to="/checkout"
						class="flex w-1/2 items-center justify-center px-4 py-1.5 rounded-lg bg-ihh-dark-salmon hover:bg-ihh-salmon hover:border-ihh-salmon text-sm text-ihh-midnight-gray font-semibold border-2 border-ihh-dark-salmon relative overflow-hidden"
						:class="{ 'opacity-80 pointer-events-none': emptyCart || store.cart?.tax === null, 'ripple-animation': store.cart?.tax === null && !emptyCart }"
					>
						<span class="relative z-10">Checkout</span>
						<div v-if="store.cart?.tax === null && !emptyCart" class="ripple-effect"></div>
					</router-link>
					<router-link
						to="/cart"
						@click="store.showBag = false"
						class="flex w-1/2 justify-center items-center gap-1 text-ihh-midnight-gray hover:text-ihh-dark-salmon transition-colors hover:cursor-pointer border-2 border-ihh-dark-salmon px-3 py-1.5 rounded-lg"
						title="View Full Cart"
					>
						<div class="flex items-center gap-1">
							<span class="text-sm">Full Cart</span>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="16"
								height="16"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								stroke-width="2"
								stroke-linecap="round"
								stroke-linejoin="round"
							>
								<path d="M7 17L17 7" />
								<path d="M7 7h10v10" />
							</svg>
						</div>
					</router-link>
				</div>
			</div>

			<div class="px-6" style="padding-bottom: calc(7rem + env(safe-area-inset-bottom, 0px))">
				<CartItems ref="cartComponent" v-slot="{ emptyBag, reduceItemQty, increaseItemQty, removeItem, reduceCertQty, increaseCertQty, removeCert }">
					<div :class="{ 'flex flex-col items-center': !store.cartTotalItems }">
						<div class="rounded-lg">
							<!-- Cart Items -->
							<BagItem
								class="my-8 first:mt-0 last:mb-0"
								v-for="{ item, quantity, variant, group, optional, optionSelections, price } in store.cart.cartItems"
								:key="item.id"
								:item="{ item, quantity, variant, group, optional, optionSelections, price }"
								:XCoverProducts="XCoverProducts"
								@product-click="handleProductClick"
								@reduce-qty="reduceItemQty"
								@increase-qty="increaseItemQty"
								@remove-item="removeItem"
							/>

							<!-- Certificates -->
							<BagCertificate
								v-if="store.cart?.certificates?.length"
								v-for="(certificate, index) in store.cart.certificates"
								:key="index"
								:certificate="certificate"
								:index="index"
								:certificateImg="certificate_img"
								@reduce-qty="reduceCertQty"
								@increase-qty="increaseCertQty"
								@remove-cert="removeCert"
							/>

							<div v-if="!store.cartTotalItems" class="text-center w-full py-10 text-lg text-ihh-gray-600 mt-48">Your bag is empty</div>
						</div>

						<template v-if="store.cartTotal">
							<FreeShippingTracker :currentCartTotal="store.cart?.cartAmountExTax || 0" class="mt-6" />
						</template>

						<UpsellScroller v-if="store.cartTotalItems" />

						<div v-if="store.cartTotalItems" class="flex flex-col text-ihh-midnight-gray">
							<!-- Subtotal -->
							<div class="flex justify-between text-base mb-1">
								<span>Subtotal</span>
								<span v-if="store.cart.tax === null">{{ store.cartSubtotal.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) }}</span>
								<span v-else>{{ (store.cart.subTotal ?? store.cartSubtotal).toLocaleString('en-US', { style: 'currency', currency: 'USD' }) }}</span>
							</div>

							<!-- Shipping -->
							<div class="w-full text-base">
								<div class="flex m-auto items-center justify-between mb-1">
									<div class="flex-grow">
										<div class="">Shipping:</div>
									</div>
									<div>
										<button
											@click="showShippingInfo = !showShippingInfo"
											class="underline cursor-pointer w-16 text-right font-normal"
											:class="checkout.shipping.selectedShipping?.id ? 'hover:text-ihh-salmon text-ihh-dark-salmon' : ''"
										>
											{{ checkout.shippingText }}
										</button>
									</div>
								</div>
								<div>
									<Shipping v-if="showShippingInfo" @hideShipping="showShippingInfo = false" class="text-ihh-midnight-gray" />
								</div>
							</div>

							<!-- Promotions -->
							<div v-if="hasPromotions" class="w-full flex m-auto items-center justify-between mb-1">
								<div class="flex-grow">
									<div class="text-[#F44631] font-ihh-bold">Discount</div>
								</div>
								<div class="w-16 text-right font-normal">-{{ promotionsTotal.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) }}</div>
							</div>

							<div class="my-4"></div>

							<!-- Coupon Code -->
							<div class="w-full text-base pb-4">
								<Coupon :style="couponStyle" />
							</div>

							<!-- Gift Certificate -->
							<div class="w-full text-base pb-4">
								<Certificate :style="certificateStyle" />
							</div>

							<!-- Total -->
							<div class="flex justify-between text-lg font-ihh-bold mt-10">
								<span>Total</span>
								<Spinner v-if="store.cart.tax === null" :small="true" class="w-4 h-4" color="border-t-ihh-salmon" />
								<span v-else>{{ checkout.displayTotal?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) }}</span>
							</div>
						</div>

						<div
							class="flex-col space-y-4 w-full pt-6"
							:class="{ 'absolute px-8': !store.cartTotalItems }"
							:style="!store.cartTotalItems ? 'bottom: calc(7rem + env(safe-area-inset-bottom, 0px))' : ''"
						>
							<button
								:class="[
									{ 'opacity-70 pointer-events-none': emptyCart || store.cart?.tax === null },
									'flex text-center items-center justify-center text-sm md:text-base text-ihh-midnight-gray py-3 px-10 w-full rounded-lg bg-ihh-dark-salmon hover:bg-ihh-salmon duration-150 tracking-wide font-semibold transition-transform active:scale-95 shadow-md hover:shadow-lg',
								]"
							>
								<router-link to="/checkout" class="w-full h-full flex items-center justify-center"> Proceed to Checkout </router-link>
							</button>
							<button
								@click="emptyBag"
								class="flex text-base w-full text-center justify-center text-ihh-midnight-gray border-3 border-ihh-salmon px-8 py-3 rounded-lg hover:bg-ihh-salmon duration-150 whitespace-nowrap font-semibold transition-transform active:scale-95 shadow-md hover:shadow-lg"
								:class="[{ 'opacity-40 pointer-events-none': emptyCart }]"
							>
								Remove all items from Bag
							</button>
						</div>
					</div>
				</CartItems>
			</div>
		</div>
	</div>
</template>

<script>
import { useCheckoutStore } from '../../stores/store.js'
import CartItems from './cart-items.vue'
import BagItem from './bag-item.vue'
import BagCertificate from './bag-certificate.vue'
import UpsellScroller from '../partials/upsell-scroller.vue'
import Shipping from '../pages/ihh-items/shipping.vue'
import FreeShippingTracker from '../partials/free-shipping-tracker.vue'
import Coupon from '../checkout/coupon.vue'
import Certificate from '../checkout/certificate.vue'

export default {
	components: {
		CartItems,
		BagItem,
		BagCertificate,
		UpsellScroller,
		Shipping,
		FreeShippingTracker,
		Coupon,
		Certificate,
		FreeShippingTracker,
	},
	inject: ['store'],
	data() {
		return {
			checkout: useCheckoutStore(),
			showShippingInfo: false,
			XCoverProducts: [3693, 3694, 3900, 3901, 4335, 4336],
			certificate_img:
				'https://cdn11.bigcommerce.com/s-nuj3c/stencil/29299650-2b9e-013d-ca92-06d759ad9f37/e/15c6ecf0-25f0-013d-e4f6-1e7f4d7752a0/img/GiftCertificate.png',
			couponStyle: {
				title: 'font-normal',
				remove: 'text-sm',
			},
			certificateStyle: {
				title: 'font-normal',
				remove: 'text-sm',
			},
		}
	},
	methods: {
		toggleBag() {
			console.log('toggleBag clicked')
			this.store.showBag = !this.store.showBag
		},
		handleProductClick(item) {
			this.store.showBag = false
			this.$router.push(`/products${item.customUrl.url}`)
		},
	},
	computed: {
		emptyCart() {
			return this.store.cart?.cartItems?.length === 0 && this.store.cart?.certificates?.length === 0
		},
		hasPromotions() {
			// Only show promotions if the discount is at least $0.01
			// This prevents showing $0 promotions due to rounding or timing issues
			return this.promotionsTotal >= 0.01
		},
		promotionsTotal() {
			const base = this.store.cart?.baseAmount || 0
			const exTax = this.store.cart?.cartAmountExTax || 0
			const coupon = this.store?.cart?.couponDiscount || 0
			const discount = base - exTax - coupon
			// Round to 2 decimal places to avoid floating-point precision issues
			return Math.round(discount * 100) / 100
		},
	},
}
</script>

<style scoped>
/* Add sliding transition */
.slide-enter-active {
	transition: all 0.3s ease;
}

.slide-leave-active {
	transition: all 0.5s ease; /* Faster removal */
}

.slide-enter {
	transform: translateY(-10px);
	opacity: 0;
}
.slide-leave-to {
	transform: translateY(-10px);
	opacity: 0;
}

/* Ripple animation styles */
.ripple-animation {
	position: relative;
}

.ripple-effect {
	position: absolute;
	top: 0;
	left: -100%;
	width: 100%;
	height: 100%;
	background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
	animation: ripple 1.5s infinite;
	z-index: 1;
}

@keyframes ripple {
	0% {
		left: -100%;
	}
	100% {
		left: 100%;
	}
}
</style>
